import { AppBase } from '@jollywise/jollygoodgame';
import { FONTS } from 'game/constants/AppFonts';
import { ASSETPACK_KEYS } from 'game/constants/LoadConstants';
import { KEYS, SCENE_MAP } from 'game/constants/SceneConstants';
import { HudConfig } from './game/constants/HudConfig';
import { ShortcutController } from './game/shortcuts/ShortcutController';
import { AssetUnloader } from '@jollywise/jollygoodgame';
import PlayerModel from './game/state/PlayerModel';

export class App extends AppBase {
  constructor(opts) {
    const params = new URLSearchParams(window.location.search);
    if (params.has('canvas')) {
      opts.config.type = Phaser.CANVAS;
    }
    super(opts);

    this.loaderType = '';
    this.allowLevelSelect = false;
    this.allowSkipping = false;
    this._language = opts.config.locale || 'en';
    console.log('_language', this._language);

    this.events.once(Phaser.Core.Events.READY, () => this.bootUp(opts));

    this.assetPack = require('../assets/assetpack.json');
    this.sandbox = params.has('sandbox'); // sandbox mode means don't save
    this.currentGame = null;
    window.game = this;
  }

  get language() {
    return this._language;
  }

  bootUp(opts) {
    this.appUrls.setPaths(opts.paths);
    const localassets = {
      local: {
        path: 'assets/locale/',
        files: [
          {
            type: 'image',
            key: 'game_logo',
            url: `logos/logo_${this._language}.png`,
          },
          {
            type: 'image',
            key: 'challenge_EcoExpert',
            url: `logos/challenge_EcoExpert_${this._language}.png`,
          },
        ],
      },
    };
    this.loader.bootGame(
      {
        boot: {
          fonts: FONTS.BOOT,
          assetpacks: [
            {
              config: { ...this.assetPack, ...localassets },
              keys: [...ASSETPACK_KEYS.BOOT, 'local'],
            },
          ],
        },
        load: {
          assetpacks: [{ config: this.assetPack, keys: ASSETPACK_KEYS.LOAD }],
        },
      },
      this.assetsLoaded.bind(this)
    );
  }

  assetsLoaded() {
    super.assetsLoaded();

    this.copy.addCopyFromJSON(this.cache.json.get('copy'));
    this.copy.setLocale(this._language);
    this.hud.setHudConfig(HudConfig);
    this.sceneController.addSceneMap(SCENE_MAP);

    const config = this.cache.json.get('config');

    this.allowExternalURL =
      config.allow_external_link && this.copy.get('url_climate_champions').length > 0;

    this._setHudEvents();
    this.startApp();
  }

  startApp() {
    this._shortcuts = new ShortcutController(this);
    this.playerModel = new PlayerModel(this.saves);
    this.showWelcome();
  }

  get model() {
    return this.playerModel;
  }

  save() {
    if (this.sandbox) return;

    this.playerModel.save();
  }

  startGame() {
    if (this.saves.hasSave('save')) {
      this.startSavedGame('save');
    } else {
      this.startNewGame('save');
    }
  }

  startNewGame(id) {
    if (this.saves.hasSave(id)) this.saves.deleteSave(id);

    this.saves.setSaveId(id);
    this.playerModel.save();
  }

  startSavedGame(id) {
    this.saves.setSaveId(id);
    this.playerModel = new PlayerModel(this.saves);

    this.showLevelSelect();
  }

  loadGame(game, loader) {
    this.currentGame = game;
    this.loaderType = loader;
    const toLoad = [
      'assets_' + this.currentGame,
      'spines_' + this.currentGame,
      'spritesheets_' + this.currentGame,
      'audiosprites_' + this.currentGame,
    ];
    if (toLoad.length == 0) {
      this.onGameLoaded();
    } else {
      this.loader.loadAssetPack(this.assetPack, toLoad, () => {
        this.onGameLoaded();
      });
    }
  }

  unloadGame(gameId) {
    // console.log('unloading assets', gameId);
    AssetUnloader.UnloadAssetpack(this, this.assetPack, [
      'assets_' + gameId,
      'spines_' + gameId,
      'spritesheets_' + gameId,
      'audiosprites_' + gameId,
    ]);
  }

  onGameLoaded() {
    this.sceneController.switchScene(KEYS[this.currentGame], {
      data: {},
    });
  }

  showLevelSelect() {
    this.sceneController.switchScene(KEYS.LevelSelect, {});
  }

  // =============================================================================================
  // SCENES
  // =============================================================================================

  returnHome() {
    if (this.paused) this.resumeGame();
    this.showWelcome();
  }

  showWelcome() {
    this.sceneController.switchScene(KEYS.Welcome, { data: {} });
  }

  // =============================================================================================
  // HUD EVENTS
  // =============================================================================================

  _setHudEvents() {
    this.hud.events.on('PAUSE_GAME', this.pauseGame, this);
    this.hud.events.on('RESUME_GAME', this.resumeGame, this);
    this.hud.events.on('RETURN_HOME', this.returnHome, this);
    this.hud.events.on('GAME_BACK', this.showLevelSelect, this);
    this.hud.events.on('SHOW_INSTRUCTIONS', this.showHowToPlay, this);
    this.hud.events.on('CLOSE_INSTRUCTIONS', this.closeHowToPlay, this);
    this.hud.events.on('TOGGLE_SOUND', this.toggleAudio, this);
    this.hud.events.on('CLOSE_OVERLAY', this._handleCloseOverlay, this);
    this.hud.events.on('SKIP_ONBOARDING', this.closeOnboarding, this);
    this.hud.events.on('START_GAME', this.startGame, this);
  }

  toggleAudio() {
    this.settings.toggleAudio();
  }

  _handleCloseOverlay() {
    this.hud.resumePreviousState();
    this.sceneController.removeOverlayScene();
  }

  // =============================================================================================
  // PAUSE / RESUME
  // =============================================================================================

  pauseGame() {
    super.pauseGame();
    this.hud.setState('PAUSE');
  }

  resumeGame() {
    super.resumeGame();
    this.hud.resumePreviousState();
  }

  // =============================================================================================
  // HOW TO PLAY
  // =============================================================================================

  showHowToPlay() {
    this.sceneController.showOverlayScene(KEYS.HowToPlay, {
      data: { content: HudConfig.howtoplay },
    });
    this.sceneController.resumeScene(KEYS.HowToPlay);
  }

  closeHowToPlay() {
    this.sceneController.pauseScene(KEYS.HowToPlay);
    this.sceneController.removeOverlayScene();
    this.hud.resumePreviousState();
  }

  // =============================================================================================
  // ONBOARDING
  // =============================================================================================

  showOnboarding(id, onComplete = null, onCompleteScope = null) {
    if (this.model.isOnboarded(id)) {
      if (onComplete) {
        onComplete.call(onCompleteScope);
      }
      return;
    }

    this.model.setOnboarded(id);
    this.onOnboardingComplete = onComplete;
    this.onOnboardingCompleteScope = onCompleteScope;
    this.sceneController.showOverlayScene(KEYS.Onboarding, {
      data: {
        content: HudConfig.onboarding,
        id,
      },
    });
    this.sceneController.resumeScene(KEYS.Onboarding);
    this.hud.setState('ONBOARDING');
  }

  closeOnboarding() {
    this.sceneController.pauseScene(KEYS.Onboarding);
    this.sceneController.removeOverlayScene();
    this.hud.resumePreviousState();
    if (this.onOnboardingComplete) {
      const obc = this.onOnboardingComplete;
      const obcs = this.onOnboardingCompleteScope;
      this.onOnboardingComplete = null;
      this.onOnboardingCompleteScope = null;
      obc.call(obcs);
    }
  }
}
